import { hoursToMilliseconds, minutesToMilliseconds } from '@gonfalon/datetime/dateFns';
import { daysToMilliseconds } from '@gonfalon/datetime/daysToMilliseconds';
import { MAX_WINDOW_DAYS } from '@gonfalon/metrics/constants';

import type { WindowUnit } from './formUtils';

export const WINDOW_UNITS: Array<{ id: WindowUnit; label: string; singular: string; maxInUnit: number }> = [
  { id: 'days', label: 'Days', singular: 'day', maxInUnit: MAX_WINDOW_DAYS },
  { id: 'hours', label: 'Hours', singular: 'hour', maxInUnit: MAX_WINDOW_DAYS * 24 },
  { id: 'minutes', label: 'Minutes', singular: 'minute', maxInUnit: MAX_WINDOW_DAYS * 24 * 60 },
];

export function getStartError(val: number | undefined, maxInUnit?: number, unitLabel?: string): string | undefined {
  if (val === undefined || Number.isNaN(val)) {
    return 'Start is required when using a custom window';
  }
  if (val < 0) {
    return 'Start must be 0 or greater';
  }
  if (!Number.isInteger(val)) {
    return 'Start must be a whole number';
  }
  if (maxInUnit !== undefined && val > maxInUnit) {
    return `Start cannot exceed ${MAX_WINDOW_DAYS} days (${maxInUnit} ${unitLabel})`;
  }
  return undefined;
}

export function getEndError(
  val: number | undefined,
  maxInUnit: number,
  unitLabel: string,
  start: number | undefined,
): string | undefined {
  if (val === undefined || Number.isNaN(val)) {
    return 'End is required when using a custom window';
  }
  if (!Number.isInteger(val)) {
    return 'End must be a whole number';
  }
  if (val > maxInUnit) {
    return `End cannot exceed ${MAX_WINDOW_DAYS} days (${maxInUnit} ${unitLabel})`;
  }
  if (typeof start === 'number' && !Number.isNaN(start) && val <= start) {
    return `End (${val}) must be greater than start (${start})`;
  }
  return undefined;
}

/** Convert ratio-term form values to API millisecond offsets, gated on windowMode. */
export function toRatioWindowOffsets(
  windowMode: 'none' | 'custom' | undefined,
  windowStart: number | undefined,
  windowEnd: number | undefined,
  windowUnit: WindowUnit = 'days',
): { windowStartOffset?: number; windowEndOffset?: number } {
  if (windowMode !== 'custom') {
    return { windowStartOffset: undefined, windowEndOffset: undefined };
  }
  return toWindowOffsets(windowStart, windowEnd, windowUnit);
}

/** Convert form values to API millisecond offsets. Returns undefined for both when no window is set. */
export function toWindowOffsets(
  windowStart: number | undefined,
  windowEnd: number | undefined,
  windowUnit: WindowUnit = 'days',
): { windowStartOffset?: number; windowEndOffset?: number } {
  if (windowStart === undefined || windowEnd === undefined) {
    return { windowStartOffset: undefined, windowEndOffset: undefined };
  }
  const toMs =
    windowUnit === 'hours'
      ? hoursToMilliseconds
      : windowUnit === 'minutes'
        ? minutesToMilliseconds
        : daysToMilliseconds;
  return {
    windowStartOffset: toMs(windowStart),
    windowEndOffset: toMs(windowEnd),
  };
}
